import React, { PropsWithChildren, useMemo } from 'react';
import { useFlags, withLDProvider } from 'launchdarkly-react-client-sdk';

import { useUserContext } from '../user/context';
import { useConfigContext } from '../../config/ConfigContext';

function ComponentWrapper({ children }: PropsWithChildren) {
  return (
    <>
      { children }
    </>
  );
}

type LaunchDarklyProps = {
  clientId: string;
};
function LaunchDarklyProvider({ children, clientId }: PropsWithChildren<LaunchDarklyProps>) {
  const { user, userOrganization } = useUserContext();

  const LDProvider: React.ComponentType<PropsWithChildren> = useMemo(() => withLDProvider({
    clientSideID: clientId,
    context: {
      key: user.id,
      email: user.email,
      custom: {
        organization: userOrganization?.name ?? 'unknown',
        sector: userOrganization?.sector ?? 'unknown'
      }
    },
    options: {
      streaming: false,
      // NOTE: If this is enabled, update `useReportFlagVariant` to handle the non-null `reason`
      evaluationReasons: false
    },
    reactOptions: {
      useCamelCaseFlagKeys: false
    }
  })(ComponentWrapper), [clientId]);

  return (
    <LDProvider>
      { children }
    </LDProvider>
  )
}

export function FeatureFlagProvider({ children }: PropsWithChildren) {
  const config = useConfigContext();
  const clientId = config?.featureFlag?.clientId;

  if (!clientId) {
    return (
      <ComponentWrapper>
        { children }
      </ComponentWrapper>
    );
  }

  return (
    <LaunchDarklyProvider clientId={clientId}>
      { children }
    </LaunchDarklyProvider>
  )
}

export function useFeatureFlags() {
  const flags = useFlags();
  const config = useConfigContext();

  return {
    ...flags,
    ...config?.featureFlag?.overrides
  };
}

export function useFeatureFlag<T>(key: string, defaultValue: T): T {
  const flags = useFeatureFlags();

  if (flags[key] !== undefined) {
    return flags[key];
  }

  return defaultValue;
}
